import React from 'react';
import { TrendingUp, CheckCircle2 } from 'lucide-react';

const predictions = [
  {
    title: 'Structure formation suppression',
    description: 'Universe Engine Theory predicts a late-time, monotone suppression of cosmic structure growth caused by a computed conversion of the dark sector into a wave-supported phase. This suppression turns on primarily at low redshift (z ≲ 1), while leaving high-redshift structure formation unchanged, and produces a smooth, characteristic downturn in the redshift evolution of S₈(z) that can be tested with tomographic weak-lensing and growth-rate measurements.'
  },
  {
    title: 'Late-time acceleration profile',
    description: "Cosmic acceleration in UET arises on a fixed relativistic gravity background and does not rely on adjustable dark-energy dynamics. Instead, late-time cosmological observations are explained through dark-sector microphysics that alters structure growth without modifying the expansion history, allowing the observed acceleration to remain consistent with a cosmological-constant–like background while still producing distinctive late-time signatures."
  },
  {
    title: 'Gravitational wave propagation',
    description: 'Universe Engine Theory preserves luminal gravitational-wave propagation on cosmological scales. Gravitational waves are predicted to travel at the speed of light with no late-time dispersion or delay, making precision multi-messenger measurements a stringent null test of the theory rather than a signal-seeking channel.'
  }
];

const ProofSection = () => {
  return (
    <section id="proof" style={{
      background: '#000000',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <h2 className="section-title" style={{
          fontSize: '48px',
          fontWeight: 600,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color: '#FFFFFF',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          THE PROOF
        </h2>

        <p className="intro-text" style={{
          fontSize: '20px',
          fontWeight: 400,
          lineHeight: 1.5,
          color: 'rgba(255, 255, 255, 0.85)',
          textAlign: 'center',
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          Elegant mathematics means nothing without experimental validation. UET makes specific, falsifiable predictions that differ from ΛCDM:
        </p>

        <div className="two-col-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center',
          marginBottom: '60px'
        }}>
          <div className="section-image" style={{
            position: 'relative',
            height: '400px',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.25)'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_omega-theory/artifacts/b4ps3xhw_20251217_2124_Futuristic%20Cosmic%20Data%20Visualization_simple_compose_01kcqqsffwf60bsgx42k1q4546.png" 
              alt="S₈(z) Sigmoid Curve Visualization"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.9
              }}
            />
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <TrendingUp size={32} color="#00FFD1" />
                <span style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#FFFFFF'
                }}>
                  S₈(z) Sigmoid Curve
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {predictions.map((pred, index) => (
              <div key={index} className="prediction-card" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                padding: '24px',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
                e.currentTarget.style.borderColor = '#00FFD1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <CheckCircle2 size={24} color="#00FFD1" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <h4 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: '#FFFFFF',
                      marginBottom: '8px'
                    }}>
                      {pred.title}
                    </h4>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      color: 'rgba(255, 255, 255, 0.85)',
                      margin: 0
                    }}>
                      {pred.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="highlight-box" style={{
          background: 'rgba(0, 255, 209, 0.1)',
          border: '2px solid #00FFD1',
          padding: '40px',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: 1.5,
            color: '#FFFFFF',
            margin: 0
          }}>
            Euclid, DESI, and the Vera Rubin Observatory will provide the data. Within five years, we'll know if the universe is truly self-selecting.
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          section {
            padding: 60px 20px !important;
          }
          .section-title {
            font-size: 36px !important;
          }
          .intro-text {
            font-size: 18px !important;
          }
          .two-col-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .section-image {
            height: 300px !important;
          }
          .highlight-box {
            padding: 30px !important;
          }
        }
        @media (max-width: 768px) {
          section {
            padding: 40px 20px !important;
          }
          .section-title {
            font-size: 32px !important;
          }
          .intro-text {
            font-size: 16px !important;
          }
          .section-image {
            height: 250px !important;
          }
          .prediction-card {
            padding: 16px !important;
          }
          .highlight-box {
            padding: 20px !important;
          }
          .highlight-box p {
            font-size: 18px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ProofSection;